import { useEffect, useState } from "react";
import { getOrder, type PublicOrder, type StorefrontConfig } from "./api.ts";

/**
 * Live order tracker: a status stepper (Placed → Accepted → On the way →
 * Delivered) plus an ETA countdown derived from the order's creation time and
 * the configured delivery window. Polls the backend until the order reaches a
 * terminal state, so the customer watches progress without leaving the app —
 * important reassurance for a cash-on-delivery flow.
 */

const STEPS = [
  { key: "pending", label: "Placed", icon: "🧾" },
  { key: "accepted", label: "Accepted", icon: "👍" },
  { key: "out_for_delivery", label: "On the way", icon: "🛵" },
  { key: "delivered", label: "Delivered", icon: "📦" },
] as const;

const TERMINAL = new Set(["delivered", "refused", "cancelled"]);
const POLL_MS = 15_000;
const TICK_MS = 30_000;

export function isActiveOrder(status: string): boolean {
  return !TERMINAL.has(status);
}

export function OrderTracker({
  order,
  config,
}: {
  order: PublicOrder;
  config: StorefrontConfig | null;
}) {
  const [current, setCurrent] = useState<PublicOrder>(order);
  const [now, setNow] = useState(() => Date.now());

  // Reset when a different order is passed in.
  useEffect(() => setCurrent(order), [order]);

  // Poll status until the order reaches a terminal state.
  useEffect(() => {
    if (TERMINAL.has(current.status)) return;
    const id = setInterval(() => {
      getOrder(current.id)
        .then(setCurrent)
        .catch(() => {
          /* transient; keep last known status */
        });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [current.id, current.status]);

  // Tick the clock so the ETA countdown stays fresh between polls.
  useEffect(() => {
    if (TERMINAL.has(current.status)) return;
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [current.status]);

  const status = current.status;
  const failed = status === "refused" || status === "cancelled";
  const activeIndex = STEPS.findIndex((s) => s.key === status);

  const etaMin = config?.deliveryEtaMinutes ?? 45;
  const target = new Date(current.createdAt).getTime() + etaMin * 60_000;
  const remainingMin = Math.ceil((target - now) / 60_000);

  let eta: string;
  if (status === "delivered") eta = "Delivered — enjoy! 🙌";
  else if (status === "refused") eta = "Delivery refused";
  else if (status === "cancelled") eta = "Order cancelled";
  else if (status === "pending") eta = "Waiting for the shop to accept…";
  else if (remainingMin > 0) eta = `Arriving in ~${remainingMin} min`;
  else eta = "Arriving any minute now 🛵";

  return (
    <section className="card tracker">
      <div className="tracker-eta">
        <span className="tracker-eta-main">{eta}</span>
        <span className="tracker-order">#{current.id.slice(-8)}</span>
      </div>

      {failed ? (
        <div className={`tracker-failed status-${status}`}>
          {status === "refused"
            ? "🚫 ID or age could not be confirmed at the door."
            : "❌ This order was cancelled."}
        </div>
      ) : (
        <ol className="tracker-steps">
          {STEPS.map((s, i) => {
            const state =
              i < activeIndex ? "past" : i === activeIndex ? "current" : "future";
            return (
              <li key={s.key} className={`tracker-step ${state}`}>
                <span className="tracker-dot">
                  {state === "past" ? "✓" : s.icon}
                </span>
                <span className="tracker-step-label">{s.label}</span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
